import React, { useState, useEffect, useCallback } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import HeroSection from "./HeroSection";
import TeamsSection from "./TeamsSection";
import RecentActivitySection from "./RecentActivitySection";
import betaAPI from "v2/api";
import showToast from "utils/toast";

// Animation variants for smooth transitions
const pageVariants = {
  initial: {
    opacity: 0,
    y: 12,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.15,
      ease: "easeIn",
    },
  },
};

// Skeleton components for loading state
const StatCardSkeleton = () => (
  <div className="flex animate-pulse items-center justify-between rounded-xl border border-[#F3F4F6] bg-white px-4 py-3">
    <div className="flex items-center gap-2.5">
      <div className="h-8 w-8 rounded-lg bg-[#E5E7EB]" />
      <div className="h-3 w-20 rounded bg-[#E5E7EB]" />
    </div>
    <div className="h-6 w-10 rounded bg-[#E5E7EB]" />
  </div>
);

const HeroSkeleton = () => (
  <div className="px-6 pb-6 pt-5">
    <div className="mb-4 flex items-center justify-between">
      {/* Left: Avatar + Title */}
      <div className="flex items-center gap-3.5">
        <div className="h-11 w-11 animate-pulse rounded-xl bg-[#E5E7EB]" />
        <div className="h-6 w-48 animate-pulse rounded bg-[#E5E7EB]" />
      </div>
      {/* Right: Settings button */}
      <div className="flex items-center">
        <div className="h-9 w-9 animate-pulse rounded-lg bg-[#E5E7EB]" />
      </div>
    </div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
      <StatCardSkeleton />
      <StatCardSkeleton />
      <StatCardSkeleton />
    </div>
  </div>
);

const TeamRowSkeleton = () => (
  <div className="flex animate-pulse items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-[#E5E7EB]" />
      <div>
        <div className="mb-1 h-4 w-28 rounded bg-[#E5E7EB]" />
        <div className="h-3 w-16 rounded bg-[#E5E7EB]" />
      </div>
    </div>
    <div className="h-3 w-24 rounded bg-[#E5E7EB]" />
  </div>
);

const TeamsSkeleton = () => (
  <div className="rounded-xl border border-[#F3F4F6] bg-white">
    <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
      <div className="h-4 w-16 animate-pulse rounded bg-[#E5E7EB]" />
      <div className="h-8 w-24 animate-pulse rounded-lg bg-[#E5E7EB]" />
    </div>
    <TeamRowSkeleton />
    <TeamRowSkeleton />
    <TeamRowSkeleton />
  </div>
);

const ActivityRowSkeleton = () => (
  <div className="flex animate-pulse items-center gap-3 border-b border-[#F3F4F6] px-4 py-3">
    <div className="h-8 w-8 rounded-lg bg-[#E5E7EB]" />
    <div className="flex-1">
      <div className="mb-1 h-3 w-40 rounded bg-[#E5E7EB]" />
      <div className="h-2.5 w-20 rounded bg-[#E5E7EB]" />
    </div>
  </div>
);

const ActivitySkeleton = () => (
  <div className="rounded-xl border border-[#F3F4F6] bg-white">
    <div className="flex items-center border-b border-[#F3F4F6] px-4 py-3">
      <div className="h-4 w-28 animate-pulse rounded bg-[#E5E7EB]" />
    </div>
    <ActivityRowSkeleton />
    <ActivityRowSkeleton />
    <ActivityRowSkeleton />
    <ActivityRowSkeleton />
  </div>
);

const LoadingSkeleton = () => (
  <motion.div
    className="min-h-full bg-[#F8F9FA]"
    variants={pageVariants}
    initial="initial"
    animate="animate"
  >
    {/* Hero Section with gradient */}
    <div
      style={{
        background:
          "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
      }}
    >
      <div className="mx-auto max-w-[1440px]">
        <HeroSkeleton />
      </div>
    </div>

    {/* Content sections */}
    <div className="mx-auto max-w-[1440px] px-6 pb-6">
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <TeamsSkeleton />
        </div>
        <div className="xl:col-span-2">
          <ActivitySkeleton />
        </div>
      </div>
    </div>
  </motion.div>
);

const WorkspaceOverview = () => {
  const { t } = useTranslation();
  const { workspaceId } = useParams();
  const { refreshKey } = useOutletContext() || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workspace, setWorkspace] = useState({
    name: "",
    teamCount: 0,
    playerCount: 0,
  });
  const [stats, setStats] = useState({
    totalPlayers: 0,
    totalTeams: 0,
    totalSessions: 0,
  });
  const [teams, setTeams] = useState([]);

  const fetchDashboard = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      setError(t("workspace.overview.noWorkspaceSelected"));
      return;
    }
    try {
      setLoading(true);

      // Fetch dashboard and workspaces list in parallel
      const [dashboardResponse, workspacesResponse] = await Promise.all([
        betaAPI.getDashboard({ workspaceId }),
        betaAPI.getWorkspaces(),
      ]);

      const data = dashboardResponse.data;
      const workspacesList = workspacesResponse.data?.workspaces || [];

      // Find workspace name from workspaces list as primary source
      const currentWorkspace = workspacesList.find(
        (w) => String(w.id) === String(workspaceId)
      );
      const workspaceName =
        currentWorkspace?.name || data.workspace_name || data.name || "";

      setStats({
        totalPlayers: data.stats?.total_players ?? 0,
        totalTeams: data.stats?.total_teams ?? 0,
        totalSessions: data.stats?.total_sessions ?? 0,
      });

      setWorkspace({
        name: workspaceName,
        teamCount: data.stats?.total_teams ?? 0,
        playerCount: data.stats?.total_players ?? 0,
      });

      // teams is paginated: { results: [...], page, page_size, total_count, total_pages }
      const teamsArray = data.teams?.results || data.teams || [];
      setTeams(
        teamsArray.map((t) => ({
          id: t.id,
          name: t.name,
          sport: t.sport,
          playerCount: t.player_count ?? 0,
          sessionCount: t.session_count ?? 0,
          lastActivity: t.last_activity || "",
          isLive: t.is_live || false,
        }))
      );

      setError(null);
    } catch (err) {
      console.error("Failed to fetch dashboard", err);
      setError(err.message);
      showToast.error(t("workspace.overview.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, t]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard, refreshKey]);

  const handleTeamCreated = () => {
    fetchDashboard();
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="flex min-h-full items-center justify-center bg-[#F8F9FA] py-20">
        <div className="text-sm text-[#DC2626]">
          {t("workspace.overview.loadFailed")}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-full bg-[#F8F9FA]"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Hero Section with gradient - full width background */}
      <div
        style={{
          background:
            "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
        }}
      >
        <div className="mx-auto max-w-[1440px]">
          <HeroSection
            workspace={{ ...workspace, id: workspaceId }}
            stats={stats}
          />
        </div>
      </div>

      {/* Content sections */}
      <div className="mx-auto max-w-[1440px] px-6 pb-6">
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-5">
          <div className="xl:col-span-3">
            <TeamsSection
              teams={teams}
              workspaceName={workspace.name}
              workspaceId={workspaceId}
              onTeamCreated={handleTeamCreated}
            />
          </div>
          <div className="xl:col-span-2">
            <RecentActivitySection />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WorkspaceOverview;
